import { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import { useCanvas } from '../context/canvasContext';
import useDimensions from '../hooks/useDimesion';
import './style.css';
import { UsersListLoading } from '../../Listings/SingleListing/loading/UsersListLoading';
import { rgbaToHex, hexToRgba, addIdToObject } from '../utils/index';

const Canvas = ({ mainBodyRef }) => {
  const { canvasRef, canvasLoading, setCanvasLoading, imagePreview, setImageHeight, setImageWidth, setSelectedShape, setFillColor, setMainColor, setStrokeColor, setStrokeWidth, setFontSize, setFontFamily, setIsBold, setIsItalic, selectedImage, isDrawing, shapeType, selectedShape } = useCanvas();
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (canvasRef && !canvasRef.current) {
      canvasRef.current = new fabric.Canvas('canvas', {
        isDrawingMode: isDrawing
      })
      canvasRef.current.setWidth(mainBodyRef?.current?.offsetWidth * 0.9);
      canvasRef.current.setHeight(mainBodyRef?.current?.offsetHeight * 0.8);
      canvasRef.current.on('selection:created', handleSelection);
      canvasRef.current.on('selection:updated', handleSelection);
      canvasRef.current.on('object:selected', (e) => {
        var obj = e.target;
        obj.setCoords()
        obj.bringToFront()
      })
      canvasRef.current.on('selection:cleared', () => {
        setSelectedShape(null);
        canvasRef.current.getObjects().forEach(obj => {
          if (obj?.controls?.deleteControl) {
            delete obj.controls.deleteControl;
          }
          if (obj?.controls?.cloneControl) {
            delete obj.controls.cloneControl;
          }
        });
      });
      canvasRef.current.on('object:modified', (e => {
        var obj = e.target;
        obj.setCoords()
      }))
      canvasRef.current.on('path:created', (e) => {
        const createdPath = e.path;
        createdPath.set({ id: 'draw' });
        canvasRef.current.renderAll();
      });
    }
  }, []);


  const onRotate = (width, height) => {
    if (canvasRef && canvasRef.current) {
      const canvas = canvasRef.current;
      const backgroundImage = canvas.backgroundImage;

      if (backgroundImage) {
        const fixedWidth = canvas.getWidth();
        const fixedHeight = canvas.getHeight();
        let currentAngle = backgroundImage.angle || 0;
        canvasRef.current.backgroundImage.set({
          angle: currentAngle,
          originX: 'center',
          originY: 'center',
          left: fixedWidth / 2,
          top: fixedHeight / 2
        });        
        const angleInRadians = fabric.util.degreesToRadians(currentAngle);
        const origWidth = backgroundImage.width;
        const origHeight = backgroundImage.height;
        const rotatedWidth = Math.abs(origWidth * Math.cos(angleInRadians)) +
          Math.abs(origHeight * Math.sin(angleInRadians));
        const rotatedHeight = Math.abs(origWidth * Math.sin(angleInRadians)) +
          Math.abs(origHeight * Math.cos(angleInRadians));
        const scaleX = fixedWidth / rotatedWidth;
        const scaleY = fixedHeight / rotatedHeight;
        const scale = Math.max(scaleX, scaleY);
        backgroundImage.set({
          scaleX: [0, 180, 360].includes(currentAngle) ? scaleX : scale,
          scaleY: [0, 180, 360].includes(currentAngle) ? scaleY : scale,
        });
        canvas.renderAll();
      }
    }
  };

  const updateCurveFromComponents = (canvas, id) => {
    const objects = canvas.getObjects().filter(o => o.id === id);
    const curve = objects.find(o => o.shapeType === 'curve');
    const start = objects.find(o => o.shapeType === 'start-circle');
    const control = objects.find(o => o.shapeType === 'control-circle');
    const end = objects.find(o => o.shapeType === 'end-circle');
    const arrowHead = objects.find(o => o.shapeType === 'arrow-head');

    if (!curve || !start || !control || !end || !arrowHead) {
      console.log('No curve found');
      return;
    }

    const p0 = start.getCenterPoint();
    const p1 = control.getCenterPoint();
    const p2 = end.getCenterPoint();

    curve.set({ path: [['M', p0.x, p0.y], ['Q', p1.x, p1.y, p2.x, p2.y]] });

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    arrowHead.set({ left: p2.x, top: p2.y, angle });

    canvas.requestRenderAll();
  }

  const toggleGroupOnLoad = (canvas, id, firstLoad = false) => {
    const allObjects = canvas.getObjects();
    const group = allObjects.find(o => o.id === id && o.type === 'group');
    if (group) {
      const objects = group._restoreObjectsState().getObjects();
      canvas.remove(group);
      let [curve, start, control, end, arrowHead] = objects;
      const p0 = objects[1].getCenterPoint();
      const p1 = objects[2].getCenterPoint();
      const p2 = objects[3].getCenterPoint();
      curve = new fabric.Path(`M ${p0.x} ${p0.y} Q ${p1.x} ${p1.y} ${p2.x} ${p2.y}`, {
        stroke: objects[0].stroke,
        strokeWidth: 5,
        fill: '',
        selectable: true,
        hasControls: false,
        hasBorders: false,
        objectCaching: false,
        id: objects[0].id,
        shape: objects[0].shape,
        shapeType: objects[0].shapeType,
        lockMovementX: true,
        lockMovementY: true
      });
      [curve, start, control, end, arrowHead].forEach(addIdToObject);
      start.set({ visible: true, selectable: true, hasControls: false, hasBorders: false });
      control.set({ visible: true, selectable: true, hasControls: false, hasBorders: false });
      end.set({ visible: false, selectable: true, hasControls: false, hasBorders: false });
      arrowHead.set({ selectable: true, hasControls: false, hasBorders: false, lockMovementX: true, lockMovementY: true });
      start.on('moving', () => updateCurveFromComponents(canvas, id));
      control.on('moving', () => updateCurveFromComponents(canvas, id));
      
      [start, control, curve, end].forEach(o => {
        o.on('mousedblclick', () => toggleGroupOnLoad(canvas, id));
      });
      canvas.add(curve, start, control, end, arrowHead);
      updateCurveFromComponents(canvas, id);
      if (firstLoad) {
        toggleGroupOnLoad(canvas, objects[0].id, true);
      }
    } else {
      const groupItems = allObjects.filter(o => o.id === id);
      if (!groupItems.length) return;

      groupItems.forEach(o => {
        if (o.shapeType === 'start-circle' || o.shapeType === 'control-circle') {
          o.set({ visible: false });
        }
        canvas.remove(o);
      });

      const newGroup = new fabric.Group(groupItems, {
        selectable: true,
        hasControls: true,
        lockScalingX: true,
        lockScalingY: true,
        id: id,
        shape: 'arrow'
      });
      fabric.Group.prototype.toObject = (function (toObject) {
        return function (propertiesToInclude) {
          return fabric.util.object.extend(toObject.call(this, propertiesToInclude), {
            id: this.id,
            shape: this.shape,
          });
        };
      })(fabric.Group.prototype.toObject);
      newGroup.on('mousedblclick', () => toggleGroupOnLoad(canvas, id));
      canvas.add(newGroup);
    }

    canvas.renderAll();
  }
  const onLoad = (fn = () => { }, width, height) => {
    setCanvasLoading(true)
    canvasRef.current.clear()
    fetch(selectedImage?.metadata)
      .then(response => response.json())
      .then(data => {
        canvasRef.current.loadFromJSON(data, () => {
          const scaleX = canvasRef.current.width / (data?.canvas?.width || 735);
          const scaleY = canvasRef.current.height / (data?.canvas?.height || 550);
          canvasRef.current.backgroundImage.set({
            top: 0,
            left: 0,
            originX: 'left',
            originY: 'top',
            scaleX: canvasRef.current.width / width,
            scaleY: canvasRef.current.height / height,
            crossOrigin: 'anonymous',
            width: width,
            height: height
          });
          canvasRef?.current?.getObjects().forEach((obj) => {
            obj.scaleX *= scaleX;
            obj.scaleY *= scaleY;
            obj.left *= scaleX;
            obj.top *= scaleY;
            obj.setCoords();
          })
          onRotate()
          canvasRef.current.getObjects().forEach(obj => {
            if (obj.type === 'group' && obj.shape === 'arrow') {
              toggleGroupOnLoad(canvasRef.current, obj.id, true);
              obj.on('mousedblclick', () => toggleGroupOnLoad(canvasRef.current, obj.id));
            }
          });
          setCanvasLoading(false)
        })
      })
      .catch(err => {
        console.error("Error loading canvas JSON:", err);
        fn()
      });

  }

  useEffect(() => {
    if (canvasRef?.current && imagePreview) {
      fabric.Image.fromURL(imagePreview,
        () => {
          var image = new Image();
          image.src = imagePreview;
          image.onload = function () {
            setCanvasLoading(false)
            setImageHeight(image.height);
            setImageWidth(image.width);
            const containerWidth = mainBodyRef?.current?.offsetWidth * 0.9;
            const containerHeight = mainBodyRef?.current?.offsetHeight * 0.8;
            const imageAspectRatio = image.width / image.height;
            const canvasWidth = image.width > containerWidth ? containerWidth : image.width;
            const canvasHeight = image.height > containerHeight ? containerHeight : image.width / imageAspectRatio;
            canvasRef.current.setWidth(containerWidth);
            canvasRef.current.setHeight(containerHeight);
            canvasRef.current.renderAll();
            const setImage = () => {
              canvasRef.current.setBackgroundImage(imagePreview, canvasRef.current.renderAll.bind(canvasRef.current), {
                top: 0,
                left: 0,
                originX: 'left',
                originY: 'top',
                scaleX: containerWidth / image.width,
                scaleY: containerHeight / image.height,
                crossOrigin: 'anonymous',
                width: image.width,
                height: image.height
              });
              setCanvasLoading(false)
            }
            if (selectedImage.metadata) {
              onLoad(setImage, image.width, image.height)
            } else {
              setImage()
            }
          };
        });
    }
  }, [imagePreview, selectedImage]);

  const handleSelection = (e) => {
    const groups = e.selected && e.selected?.length > 1
    if (groups) {
      const activeSelection = canvasRef.current.getActiveObject()?.getObjects();
      const hasShapeType = activeSelection.some(obj => obj.shapeType);
      if (hasShapeType) {
        canvasRef.current.discardActiveObject();
      }
      setSelectedShape(null)
    } else {
      const selected = e.selected[0];
      if (selected?.shapeType) {
        return;
      }
      setSelectedShape(selected);
      if (selected.type !== 'group' && selected.type !== 'textbox' && selected.id !== 'customIcon' && selected.id !== 'garbageIcon' && selected.id !== 'customIconNew' && selected.id !== 'draw' && selected?.type !== 'image') {
        if (selected.fill?.includes('#')) {
          setFillColor(selected.fill);
          setMainColor(hexToRgba(selected.fill, 1));
        } else {
          setFillColor(rgbaToHex(selected.fill));
          setMainColor(selected.fill);
        }
        setStrokeColor(selected.stroke);
        setStrokeWidth(selected.strokeWidth);
      }
      else if (selected.type === 'line') {
        setFillColor(selected.stroke);
        setMainColor(hexToRgba(selected.stroke, 1));
        setStrokeWidth(selected.strokeWidth);
      }
      else if (selected.id === 'customIcon' || selected.id === 'garbageIcon') {
        setFillColor(rgbaToHex(selected.fill));
        setMainColor(selected?.fill);
      }
      else if (selected.id === 'customIconNew') {
        const obj = selected.getObjects();
        obj.forEach((path) => {
          if (path.path) {
            setFillColor(rgbaToHex(path.fill));
            setMainColor(path?.fill);
          }
        });
      }
      else if (selected.id === 'draw') {
        setStrokeColor(selected?.stroke);
        setStrokeWidth(selected?.strokeWidth)
      }
      else if (selected.type === 'group' && selected.id !== 'customIconNew' && selected.shape !== 'arrow') {
        if (selected.getObjects()[0]?.fill?.includes('#')) {
          setFillColor(selected.getObjects()[0]?.fill);
          setMainColor(hexToRgba(selected.getObjects()[0]?.fill, 1));
        } else {
          setFillColor(rgbaToHex(selected.getObjects()[0]?.fill));
          setMainColor(selected.getObjects()[0]?.fill);
        }
        setStrokeColor(selected.getObjects()[0]?.stroke);
      }
      else if (selected.type === 'textbox') {
        setFontSize(selected.fontSize);
        setFontFamily(selected.fontFamily);
        setIsBold(selected.fontWeight === 'bold');
        setIsItalic(selected.fontStyle === 'italic');
        setFillColor(selected.fill);
      }
      else if (selected.shape === 'arrow' && selected.type === 'group') {
        setStrokeColor(selected?._objects[0]?.stroke);
      }
      else {
        setFillColor('#ffffff')
        setMainColor('rgba(255, 255, 255, 1)')
        setStrokeColor('#000000')
      }
    }
  };

  const handleZoom = (e) => {
    e.preventDefault(); // Prevent default scrolling
    const zoomFactor = 0.1;
    // Fabric.js wheel delta is opposite of native wheel delta
    let newZoom = zoom + (e.e.deltaY > 0 ? -zoomFactor : zoomFactor);

    newZoom = Math.min(Math.max(0.5, newZoom), 3); // Clamp zoom between 0.1 and 3

    setZoom(newZoom);

    if (canvasRef.current) {
      const pointer = canvasRef.current.getPointer(e.e);
      canvasRef.current.zoomToPoint(
        new fabric.Point(pointer.x, pointer.y),
        newZoom
      );
      canvasRef.current.renderAll();
    }
  };

  useEffect(() => {
    if (canvasRef.current) {

      fabric.Object.prototype.set({
        cornerColor: '#0066cc',
        cornerStrokeColor: '#fff',
        cornerSize: 10,
        cornerStyle: 'square',
        transparentCorners: false,
        borderColor: '#fff',
        strokeWidth: 2
      });

      canvasRef.current.renderAll();
    }
  }, [canvasRef.current]);

  return (
    <>
      <div ref={mainBodyRef} className='wrapper'>
        {canvasLoading && <UsersListLoading onTop={true} />}
        <canvas id="canvas" className={'canvas'} />
        <>
        {((isDrawing && shapeType) || selectedImage?.reject_reason) && (
          <span className="bg-danger text-white fw-bold p-2 reject-reason">
            {selectedImage?.reject_reason && !isDrawing ? (
              <>Previous changes were rejected: {selectedImage.reject_reason}</>
            ) : shapeType === 'polygonshape' ? (
              'Drawing mode is active. Start clicking to create your polygon shape. To stop, double-click on the canvas.'
            ) : (
              <>Freehand drawing is active. To stop drawing, click on the pen icon or click on the <strong>Stop Drawing</strong> from the sidebar.</>
            )}
          </span>
        )}
        {
          (selectedShape && selectedShape?.shape === 'arrow' && !selectedImage?.reject_reason) && (
            <span className="bg-danger text-white fw-bold p-2 reject-reason">
              Double-click on the arrow to edit control points and double-click on the controls again to stop editing.
            </span>
          )
        }
        </>
      </div>
    </>
  );
}


export default Canvas